import React, { forwardRef, useMemo } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import theme, {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

import { INDENT_UNIT } from '../consts';

type IndentType = 'Ancestor' | 'Parent' | 'Self';

const getGuideLineStyle = ({
  $type,
  $isExpandable,
  $isLastAtDepth,
}: {
  $type: IndentType;
  $isExpandable: boolean;
  $isLastAtDepth?: boolean;
}): StyleProp => {
  if ($type === 'Self') {
    // Parent handles the horizontal line, nothing to do here
    return {};
  }

  return {
    ...($type === 'Parent' && {
      '&::before': {
        borderTop: `1px dashed ${theme.tokens.colors.border.value}`,
        opacity: 0.5,
        display: 'inline-block',
        content: `""`,
        left: INDENT_UNIT / 2,
        margin: 0,
        position: `absolute`,
        top: 20,
        width: $isExpandable ? INDENT_UNIT / 3 : INDENT_UNIT,
      },
    }),
    '&::after': {
      borderLeft: `1px dashed ${theme.tokens.colors.border.value}`,
      opacity: 0.5,
      display: 'inline-block',
      content: `""`,
      height: $type === 'Parent' && $isLastAtDepth ? '50%' : '100%',
      left: INDENT_UNIT / 2,
      position: 'absolute',
      top: 0,
    },
  };
};

type IndentProps = StylableComponent<HTMLDivElement> & {
  guidelines?: boolean;
  depth?: number;
  isExpandable: boolean;
  isLastAtDepth?: boolean;
  nodeId: string;
};

const getIndentTypes = ({
  depth,
  isExpandable,
}: {
  depth: number;
  isExpandable: boolean;
}): { type: IndentType; position: number }[] => {
  const indentDepth = isExpandable ? depth : (depth ?? 0) + 1;

  return [...Array(indentDepth).keys()].map((_, i, arr) => {
    let type: IndentType = 'Ancestor';
    if (i === arr.length - 1) {
      type = isExpandable ? 'Parent' : 'Self';
    } else if (i === arr.length - 2) {
      type = isExpandable ? 'Ancestor' : 'Parent';
    }
    return { type, position: i };
  });
};

const StyledIndent = styledC.div`
  position: relative;
  min-width: ${INDENT_UNIT}px;
  width: ${INDENT_UNIT}px;
  height: 100%;
`;

const StyledGuidelineIndent = styledC(StyledIndent)<{
  $type: IndentType;
  $isExpandable: boolean;
  $isLastAtDepth?: boolean;
}>`
  ${getGuideLineStyle}
`;

const Indent = forwardRef<HTMLDivElement, IndentProps>(
  (
    {
      guidelines = true,
      depth = 0,
      nodeId,
      isExpandable,
      isLastAtDepth,
      ...props
    },
    ref,
  ) => {
    const indentTypes = useMemo(
      () => getIndentTypes({ depth, isExpandable }),
      [depth, isExpandable],
    );

    const indentProps = {
      ref,
      ['data-testid']: 'TreeNodeIndent',
      ...props,
    };

    return (
      <>
        {indentTypes.map(({ type, position }) =>
          guidelines ? (
            <StyledGuidelineIndent
              className={type}
              key={`${nodeId}${position}`}
              $type={type}
              $isExpandable={isExpandable}
              $isLastAtDepth={isLastAtDepth}
              {...indentProps}
            />
          ) : (
            <StyledIndent
              className={type}
              key={`${nodeId}${position}`}
              type={type}
              {...indentProps}
            />
          ),
        )}
      </>
    );
  },
);

Indent.displayName = 'Indent';

Indent.propTypes = {
  /* style object to add to the wrapper of the node */
  style: stylePropType,
  /* className to add to the wrapper of the node */
  className: PropTypes.string,
  /* Current depth of this node, which offsets the node to the right to indicate nesting */
  depth: PropTypes.number,
};

export default Indent;
